import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Checkbox,
  FormControlLabel
} from '@mui/material';
import { adminAPI, crmAPI, eventBatchAPI, trekAPI } from '../services/api';
import { REGISTRATION_LEAD_STATUS_OPTIONS } from '../constants/registrationLeadStatuses.js';
import {
  CATEGORY_LABEL,
  UPCOMING_REGISTRATION_TYPE,
  formatBatchDates,
  formatLeadDate,
  resolveLeadDisplay
} from '../utils/leadRequestDisplay.js';
import {
  leadEditBatchListParams,
  mergeCurrentBatchForLeadSelect,
  resolveLeadEditBatchId,
  resolveLeadEditTrekId
} from '../utils/leadRequestEditBatches.js';
import { isValidPhoneInput, normalizePhoneInput, phoneInputProps } from '../utils/phoneInput.js';
import { StatusChip } from './ui';

function batchOptionLabel(batch) {
  const dates = formatBatchDates(batch.startDate, batch.endDate);
  const code = batch.code ? ` (${batch.code})` : '';
  return `${dates}${code}`;
}

function emptyForm() {
  return {
    name: '',
    phone: '',
    reason: '',
    newNote: '',
    leadStatus: '',
    batchId: ''
  };
}

function formatNoteDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString();
}

const emptySelectEm = <em style={{ color: '#94a3b8' }}>—</em>;

export default function LeadRequestEditDialog({ open, lead, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [syncAllForPhone, setSyncAllForPhone] = useState(true);

  const isUpcomingReg = lead?.leadType === UPCOMING_REGISTRATION_TYPE;
  const isCrmLead = lead && !isUpcomingReg;
  const bookingId = lead?.bookingId;
  const trekSlug = lead?.eventSlug || null;

  const { data: bookingRes, isLoading: bookingLoading } = useQuery({
    queryKey: ['lead-request-edit-booking', bookingId],
    queryFn: () => adminAPI.getBooking(bookingId),
    enabled: open && isUpcomingReg && !!bookingId
  });

  const { data: reasonsRes } = useQuery(['crm-reasons'], () => crmAPI.getReasons(), {
    enabled: open && isCrmLead
  });
  const reasons = reasonsRes?.data?.data || [];
  const reasonOptions = useMemo(() => {
    const cur = String(form.reason || '').trim();
    if (!cur || reasons.includes(cur)) return reasons;
    return [...reasons, cur];
  }, [reasons, form.reason]);

  const { data: activityRes, isLoading: activityLoading } = useQuery({
    queryKey: ['lead-activity', lead?.leadType, lead?.id],
    queryFn: () => crmAPI.getLeadActivity(lead.leadType, lead.id),
    enabled: open && !!lead?.leadType && lead?.id != null
  });

  const booking = bookingRes?.data?.data;
  const activityNotes = activityRes?.data?.data?.notes || [];

  const displayNotes = useMemo(() => {
    const notes = [...activityNotes];
    const legacy = String(lead?.message || '').trim();
    if (legacy) {
      const alreadyLogged = notes.some(
        (n) => String(n.body || '').trim() === legacy
      );
      if (!alreadyLogged) {
        notes.unshift({
          id: 'legacy-message',
          body: legacy,
          createdByName: 'Historical note',
          createdAt: lead?.createdAt || null
        });
      }
    }
    return notes;
  }, [activityNotes, lead?.message, lead?.createdAt]);

  const { data: treksRes } = useQuery(
    ['lead-edit-treks'],
    () => trekAPI.list({ page: 1, limit: 500, isActive: true }),
    { enabled: open && !!trekSlug }
  );

  const trekIdFromSlug = useMemo(() => {
    if (!trekSlug) return null;
    const rows = treksRes?.data?.data || [];
    const hit = rows.find((t) => String(t.slug) === String(trekSlug));
    return hit?.id ?? null;
  }, [treksRes, trekSlug]);

  const effectiveTrekId = useMemo(
    () => resolveLeadEditTrekId({ lead, booking, trekIdFromSlug }),
    [lead, booking, trekIdFromSlug]
  );

  const { data: batchesRes, isLoading: batchesLoading } = useQuery(
    ['lead-edit-batches', effectiveTrekId],
    () => eventBatchAPI.list(leadEditBatchListParams(effectiveTrekId)),
    { enabled: open && !!effectiveTrekId && (isCrmLead || isUpcomingReg) }
  );

  const batches = batchesRes?.data?.data || batchesRes?.data?.rows || [];
  const display = resolveLeadDisplay(lead, booking);

  const batchesForSelect = useMemo(() => {
    const trekKey = effectiveTrekId ? String(effectiveTrekId) : '';
    const forTrek = trekKey
      ? batches.filter((b) => String(b.trekId ?? b.trek?.id) === trekKey)
      : batches;
    return mergeCurrentBatchForLeadSelect(forTrek, {
      batchId: form.batchId,
      booking,
      lead
    });
  }, [batches, effectiveTrekId, form.batchId, booking, lead]);

  useEffect(() => {
    if (!open || !lead) return;
    setError('');
    setForm({
      name: lead.name && lead.name !== '—' ? lead.name : '',
      phone: normalizePhoneInput(lead.phone),
      reason: lead.reason || '',
      newNote: '',
      leadStatus: lead.leadStatus || '',
      batchId: resolveLeadEditBatchId(lead, booking)
    });
    setSyncAllForPhone(true);
  }, [open, lead, booking]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!lead) return;
    setError('');

    if (!form.name.trim()) {
      setError('Name is required.');
      return;
    }
    if (!isValidPhoneInput(form.phone)) {
      setError('Enter a valid 10-digit mobile number starting with 6–9.');
      return;
    }

    const payload = {
      name: form.name.trim(),
      phone: normalizePhoneInput(form.phone),
      leadStatus: form.leadStatus || null,
      batchId: form.batchId ? Number(form.batchId) : null,
      ...(lead?.samePhoneCount > 1 ? { syncAllForPhone } : {})
    };

    const trimmedNote = form.newNote.trim();
    if (trimmedNote) {
      payload.newNote = trimmedNote;
    }

    if (isCrmLead) {
      payload.reason = form.reason.trim() || null;
    }

    setSaving(true);
    try {
      await crmAPI.updateLead(lead.leadType, lead.id, payload);
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const showEventDatePicker = isUpcomingReg || (isCrmLead && trekSlug);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
        Edit lead — {lead?.name || ''}
      </DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers sx={{ pt: 2.5, pb: 2.5 }}>
          {error ? (
            <Typography color="error" variant="body2" sx={{ mb: 2 }}>
              {error}
            </Typography>
          ) : null}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                Category
              </Typography>
              <StatusChip
                status={lead?.leadType}
                label={CATEGORY_LABEL[lead?.leadType] || lead?.leadType || '—'}
              />
            </Box>

            <TextField
              fullWidth
              size="small"
              label="Name"
              required
              value={form.name}
              onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            />

            <TextField
              fullWidth
              size="small"
              label="Phone"
              required
              value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: normalizePhoneInput(e.target.value) }))}
              inputProps={phoneInputProps}
              helperText="10-digit mobile starting with 6–9"
            />

            <TextField
              fullWidth
              size="small"
              label="Trek/Event"
              value={display.trekEvent}
              disabled
            />

            {isUpcomingReg ? (
              <TextField
                fullWidth
                size="small"
                label="Package"
                value={bookingLoading && bookingId ? 'Loading…' : display.packageName}
                disabled
              />
            ) : null}

            {showEventDatePicker ? (
              <FormControl fullWidth size="small">
                <InputLabel id="lead-edit-batch-label" shrink>
                  Date of event
                </InputLabel>
                <Select
                  labelId="lead-edit-batch-label"
                  label="Date of event"
                  displayEmpty
                  notched
                  value={form.batchId}
                  onChange={(e) => setForm((p) => ({ ...p, batchId: e.target.value }))}
                  disabled={batchesLoading}
                  renderValue={(selected) => {
                    if (!selected) {
                      return (
                        <em style={{ color: '#94a3b8' }}>
                          {batchesLoading ? 'Loading…' : '— Select upcoming batch —'}
                        </em>
                      );
                    }
                    const batch = batchesForSelect.find((b) => String(b.id) === String(selected));
                    return batch ? batchOptionLabel(batch) : String(selected);
                  }}
                >
                  <MenuItem value="">
                    <em>{batchesLoading ? 'Loading…' : '— Select upcoming batch —'}</em>
                  </MenuItem>
                  {batchesForSelect.map((b) => (
                    <MenuItem key={b.id} value={String(b.id)}>
                      {batchOptionLabel(b)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                fullWidth
                size="small"
                label="Date of event"
                value={display.eventDate}
                disabled
              />
            )}

            {isCrmLead ? (
              <FormControl fullWidth size="small">
                <InputLabel id="lead-edit-reason-label" shrink>
                  Reason
                </InputLabel>
                <Select
                  labelId="lead-edit-reason-label"
                  label="Reason"
                  displayEmpty
                  notched
                  value={form.reason}
                  onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                  renderValue={(selected) => (selected ? selected : emptySelectEm)}
                >
                  <MenuItem value="">—</MenuItem>
                  {reasonOptions.map((r) => (
                    <MenuItem key={r} value={r}>
                      {r}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField fullWidth size="small" label="Reason" value="—" disabled />
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Notes
              </Typography>
              {activityLoading ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Loading notes…
                </Typography>
              ) : displayNotes.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  No notes yet.
                </Typography>
              ) : (
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                    mb: 1.5,
                    maxHeight: 200,
                    overflowY: 'auto',
                    p: 1.5,
                    bgcolor: '#f4f6fb',
                    borderRadius: 2,
                    border: '1px solid #e2e8f0'
                  }}
                >
                  {displayNotes.map((note) => (
                    <Box key={note.id}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {note.body}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {note.createdByName || 'Unknown'}
                        {note.createdAt ? ` · ${formatNoteDate(note.createdAt)}` : ''}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
              <TextField
                fullWidth
                size="small"
                label="Add note"
                value={form.newNote}
                onChange={(e) => setForm((p) => ({ ...p, newNote: e.target.value }))}
                multiline
                minRows={2}
                placeholder="New notes are appended — existing notes cannot be edited."
              />
            </Box>

            <TextField
              fullWidth
              size="small"
              label="Lead Date"
              value={formatLeadDate(lead?.createdAt)}
              disabled
            />

            {isUpcomingReg ? (
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.75, display: 'block' }}>
                  Registration status
                </Typography>
                <StatusChip status={booking?.registration?.status} label={display.registrationStatus} />
              </Box>
            ) : null}

            <FormControl fullWidth size="small">
              <InputLabel id="lead-edit-status-label" shrink>
                Lead status
              </InputLabel>
              <Select
                labelId="lead-edit-status-label"
                label="Lead status"
                displayEmpty
                notched
                value={form.leadStatus}
                onChange={(e) => setForm((p) => ({ ...p, leadStatus: e.target.value }))}
                renderValue={(selected) => {
                  if (!selected) return <em style={{ color: '#94a3b8' }}>Not set</em>;
                  const opt = REGISTRATION_LEAD_STATUS_OPTIONS.find((o) => o.value === selected);
                  return opt?.label || selected;
                }}
              >
                <MenuItem value="">
                  <em>Not set</em>
                </MenuItem>
                {REGISTRATION_LEAD_STATUS_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {lead?.samePhoneCount > 1 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: '#fffbeb',
                  border: '1px solid #fde68a'
                }}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={syncAllForPhone}
                      onChange={(e) => setSyncAllForPhone(e.target.checked)}
                      size="small"
                      color="primary"
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ fontSize: '13px', fontWeight: 500, color: '#92400e' }}>
                      Apply this status to all {lead.samePhoneCount} inquiries from this customer ({lead.phone})
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={onClose} disabled={saving} color="inherit">
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={18} color="inherit" />
                Saving…
              </Box>
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
